import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Briefcase, Plus, X, FileDown, Pencil, Trash2, Paperclip, RefreshCw, Send,
  Download, Upload, UserPlus, UserMinus, MessageCircle,
} from "lucide-react";
import DepartmentFilter, { useDepartmentFilter } from "@/components/DepartmentFilter";
import FirmFilter from "@/components/FirmFilter";
import { compressImage } from "@/lib/imageCompressor";
import { fmtDateTime } from "@/lib/format";
import EmployeeStatusFilter from "@/components/EmployeeStatusFilter";
import EmployeePicker from "@/components/EmployeePicker";
import { usePermissions } from "@/contexts/PermissionsContext";
import ModuleHint from "@/components/ModuleHint";

const EVENT_OPTIONS = [
  { value: "anmeldung", label: "Anmeldung", Icon: UserPlus,
    badgeCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    cardActiveCls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  { value: "abmeldung", label: "Abmeldung", Icon: UserMinus,
    badgeCls: "bg-red-500/15 text-red-300 border-red-500/30",
    cardActiveCls: "bg-red-500/15 border-red-500/40 text-red-300" },
  { value: "sonstige", label: "Sonstige Mitteilung", Icon: MessageCircle,
    badgeCls: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    cardActiveCls: "bg-blue-500/15 border-blue-500/40 text-blue-300" },
];

export default function Buchhaltung() {
  const { can } = usePermissions();
  const canCreate = can("mitarbeiter", "edit");
  const canEdit = can("mitarbeiter", "edit");
  const canDelete = can("mitarbeiter", "delete");

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const [deptFilter, setDeptFilter] = useDepartmentFilter("buchhaltung");
  const [empStatus, setEmpStatus] = useState("aktiv");
  const [eventFilter, setEventFilter] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (eventFilter) params.event_type = eventFilter;
      if (empFilter) params.employee_id = empFilter;
      if (deptFilter) params.department_id = deptFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/buchhaltung-mails", { params });
      setItems(data);
    } catch { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventFilter, empFilter, deptFilter, dateFrom, dateTo]);
  useEffect(() => {
    api.get("/employees", { params: empStatus ? { status: empStatus } : {} })
      .then(({ data }) => setEmployees(data));
  }, [empStatus]);

  const empName = (id) => {
    const e = employees.find(x => x.id === id);
    return e ? `${e.first_name} ${e.last_name}` : "?";
  };
  const eventMeta = (t) => EVENT_OPTIONS.find(x => x.value === t) || EVENT_OPTIONS[2];

  async function downloadPdf(b) {
    try {
      const res = await api.get(`/buchhaltung-mails/${b.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `buchhaltung_${b.event_type}_${empName(b.employee_id)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF-Download fehlgeschlagen"); }
  }

  async function removeItem(b) {
    if (!window.confirm(`Buchhaltungs-E-Mail ${eventMeta(b.event_type).label} für ${empName(b.employee_id)} wirklich löschen?`)) return;
    try {
      await api.delete(`/buchhaltung-mails/${b.id}`);
      toast.success("Eintrag gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  }

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(b =>
      (empName(b.employee_id) || "").toLowerCase().includes(q) ||
      (b.subject || "").toLowerCase().includes(q) ||
      (b.position || "").toLowerCase().includes(q)
    );
  // eslint-disable-next-line
  }, [items, search, employees]);

  return (
    <div className="p-6 md:p-8" data-testid="buchhaltung-page">
      <ModuleHint moduleKey="buchhaltung" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-zinc-500"/> Buchhaltungs-E-Mails
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {items.length} Einträge · Anmeldung / Abmeldung / Mitteilungen an externe Buchhaltung
          </p>
        </div>
        {canCreate && (
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="bh-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded">
            <Plus className="w-4 h-4"/> Neu hinzufügen
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-3 mb-4 flex flex-wrap gap-2 items-center">
        <FirmFilter compact />
        <DepartmentFilter value={deptFilter} onChange={setDeptFilter} module="buchhaltung" testid="bh-dept-filter"/>
        <EmployeeStatusFilter value={empStatus} onChange={setEmpStatus} testid="bh-empstatus-filter" size="sm"/>
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
          data-testid="bh-event-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100">
          <option value="">Alle Typen</option>
          {EVENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)}
          data-testid="bh-emp-filter"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 max-w-[200px]">
          <option value="">Alle Mitarbeiter</option>
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="bh-date-from"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="bh-date-to"
          className="px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100"/>
        <input placeholder="Suchen…" value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="bh-search"
          className="ml-auto px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 w-48"/>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Laden…</div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            <Briefcase className="w-8 h-8 mx-auto mb-2 text-zinc-700"/>
            Noch keine Buchhaltungs-E-Mails erfasst.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Typ</th>
              <th className="px-4 py-3 text-left">Mitarbeiter</th>
              <th className="px-4 py-3 text-left">Betreff</th>
              <th className="px-4 py-3 text-left">Datum</th>
              <th className="px-4 py-3 text-center">Anh.</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{filteredItems.map(b => {
              const m = eventMeta(b.event_type);
              const { Icon } = m;
              const atts = (b.attachments || []).length;
              return (
                <tr key={b.id} data-testid={`bh-row-${b.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold uppercase border ${m.badgeCls}`}>
                      <Icon className="w-3 h-3"/> {m.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-200">{empName(b.employee_id)}</td>
                  <td className="px-4 py-2.5 text-zinc-300 max-w-[320px] truncate" title={b.subject}>{b.subject}</td>
                  <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs">{b.event_date || "—"}</td>
                  <td className="px-4 py-2.5 text-center">
                    {atts > 0 ? <span className="inline-flex items-center gap-0.5 text-blue-400 text-[11px]"><Paperclip className="w-3 h-3"/> {atts}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-flex text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${
                      b.status === "gesendet" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : b.status === "fehlgeschlagen" ? "bg-red-500/15 text-red-300 border border-red-500/30"
                      : "bg-zinc-700/50 text-zinc-300 border border-zinc-600/40"
                    }`}>{b.status || "entwurf"}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => downloadPdf(b)} data-testid={`bh-pdf-${b.id}`}
                        title="PDF herunterladen"
                        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                        <FileDown className="w-3.5 h-3.5"/> PDF
                      </button>
                      {canEdit && (
                        <button onClick={() => { setEditing(b); setShowForm(true); }} data-testid={`bh-edit-${b.id}`}
                          title="Bearbeiten"
                          className="inline-flex items-center text-zinc-400 hover:text-zinc-100">
                          <Pencil className="w-3.5 h-3.5"/>
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => removeItem(b)} data-testid={`bh-delete-${b.id}`}
                          title="Löschen"
                          className="inline-flex items-center text-zinc-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <BuchhaltungForm
          employees={employees}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// BuchhaltungForm
// ============================================================
function BuchhaltungForm({ employees, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const today = new Date().toISOString().slice(0, 10);
  const [config, setConfig] = useState({ default_to: "", default_cc: [] });
  const [form, setForm] = useState(() => editing ? {
    employee_id: editing.employee_id || "",
    event_type: editing.event_type || "anmeldung",
    event_date: editing.event_date || today,
    hours_per_week: editing.hours_per_week ?? 40,
    position: editing.position || "",
    termination_reason: editing.termination_reason || "",
    subject: editing.subject || "",
    body: editing.body || "",
    to_email: editing.to_email || "",
    cc_emails: editing.cc_emails || [],
    attachments: editing.attachments || [],
    status: editing.status || "entwurf",
  } : {
    employee_id: "", event_type: "anmeldung",
    event_date: today, hours_per_week: 40, position: "", termination_reason: "",
    subject: "", body: "",
    to_email: "", cc_emails: [],
    attachments: [], status: "entwurf",
  });
  const [newCc, setNewCc] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Load config defaults once (for new entries only)
  useEffect(() => {
    api.get("/settings/buchhaltung").then(({ data }) => {
      setConfig({ default_to: data.default_to || "", default_cc: data.default_cc || [] });
      if (!isEdit) {
        setForm(f => ({
          ...f,
          to_email: f.to_email || data.default_to || "",
          cc_emails: f.cc_emails.length > 0 ? f.cc_emails : (data.default_cc || []),
        }));
      }
    }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  // User-CCs shown as chips (exclude defaults which are rendered as STD badges)
  const userCc = useMemo(() => {
    const defSet = new Set((config.default_cc || []).map(x => x.toLowerCase()));
    return (form.cc_emails || []).filter(e => !defSet.has(e.toLowerCase()));
  }, [form.cc_emails, config.default_cc]);

  async function loadTemplate(force = false, overrides = {}) {
    const eid = overrides.employee_id || form.employee_id;
    if (!eid) return;
    if (!force && (form.subject || form.body)) return;
    setLoadingTpl(true);
    try {
      const { data } = await api.get("/buchhaltung-mails/template", {
        params: {
          employee_id: eid,
          event_type: overrides.event_type || form.event_type,
          event_date: overrides.event_date || form.event_date,
          hours_per_week: overrides.hours_per_week ?? form.hours_per_week,
          position: overrides.position ?? form.position,
          termination_reason: overrides.termination_reason ?? form.termination_reason,
        },
      });
      setForm(f => ({ ...f, subject: data.subject, body: data.body }));
    } catch { toast.error("Vorlage konnte nicht geladen werden"); }
    finally { setLoadingTpl(false); }
  }

  // Auto-load template when employee first picked (create mode)
  useEffect(() => {
    if (!isEdit && form.employee_id) loadTemplate(false);
  // eslint-disable-next-line
  }, [form.employee_id]);

  // Reset body/subject when event_type changes (so template matches)
  function changeEventType(v) {
    setForm(f => ({ ...f, event_type: v, subject: "", body: "" }));
    // Pass explicit event_type to bypass stale closure
    loadTemplate(true, { event_type: v });
  }

  function addCc() {
    const v = (newCc || "").trim();
    if (!v || !v.includes("@")) { toast.error("Ungültige E-Mail"); return; }
    if ((config.default_cc || []).map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("Diese Adresse ist bereits als Standard-Kopie eingestellt");
      return;
    }
    if (userCc.map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("E-Mail bereits in Liste");
      return;
    }
    setForm(f => ({ ...f, cc_emails: [...(f.cc_emails || []), v] }));
    setNewCc("");
  }
  function removeCc(email) {
    setForm(f => ({ ...f, cc_emails: f.cc_emails.filter(e => e !== email) }));
  }

  async function uploadFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!editing?.id) { toast.error("Bitte zuerst speichern"); e.target.value = ""; return; }
    setUploading(true);
    try {
      for (const f of files) {
        // Phase 5Z.42 — compress image attachments (PDFs pass through untouched)
        const out = await compressImage(f, { maxDimension: 1920, jpegQuality: 0.85 });
        const fd = new FormData();
        fd.append("file", out);
        const { data } = await api.post(`/buchhaltung-mails/${editing.id}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setForm(ff => ({ ...ff, attachments: [...(ff.attachments || []), data] }));
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); e.target.value = ""; }
  }
  async function removeAttachment(aid) {
    if (!editing?.id) return;
    try {
      await api.delete(`/buchhaltung-mails/${editing.id}/attachments/${aid}`);
      setForm(f => ({ ...f, attachments: f.attachments.filter(a => a.id !== aid) }));
      toast.success("Anhang entfernt");
    } catch { toast.error("Entfernen fehlgeschlagen"); }
  }
  async function downloadAttachment(a) {
    try {
      const res = await api.get(`/buchhaltung-mails/${editing.id}/attachments/${a.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const x = document.createElement("a");
      x.href = url; x.download = a.name; x.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  }

  async function save() {
    if (!form.employee_id) { toast.error("Mitarbeiter pflicht"); return; }
    if (!form.subject?.trim()) { toast.error("Betreff pflicht"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/buchhaltung-mails/${editing.id}`, form);
        toast.success("Gespeichert");
      } else {
        await api.post("/buchhaltung-mails", form);
        toast.success("Erstellt");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  async function sendNow() {
    if (!isEdit || !editing?.id) { toast.error("Bitte zuerst speichern"); return; }
    if (!form.to_email?.trim() || !form.to_email.includes("@")) { toast.error("Empfänger fehlt"); return; }
    if (!form.subject?.trim()) { toast.error("Betreff pflicht"); return; }
    const ok = window.confirm(
      `E-Mail jetzt verschicken?\n\nAn: ${form.to_email}\nKopie: ${(form.cc_emails || []).join(", ") || "—"}\n\nPDF + ${form.attachments.length} Anhang/Anhänge werden mitgesendet.`
    );
    if (!ok) return;
    setSending(true);
    try {
      await api.put(`/buchhaltung-mails/${editing.id}`, form);
      const { data } = await api.post(`/buchhaltung-mails/${editing.id}/send`);
      toast.success(`Versendet an ${data.to}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setSending(false); }
  }

  const isAnmeldung = form.event_type === "anmeldung";
  const isAbmeldung = form.event_type === "abmeldung";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="bh-form-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-4xl max-h-[94vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <h3 className="font-display font-semibold text-zinc-50 inline-flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-400"/>
            {isEdit ? "Buchhaltungs-E-Mail bearbeiten" : "Neue Buchhaltungs-E-Mail"}
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="bh-form-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Event type selector — 3 cards */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Art der Mitteilung *</label>
            <div className="grid grid-cols-3 gap-2">
              {EVENT_OPTIONS.map(o => {
                const { Icon } = o;
                const active = form.event_type === o.value;
                return (
                  <button key={o.value} onClick={() => changeEventType(o.value)}
                    data-testid={`bh-event-${o.value}`}
                    className={`p-3 text-sm font-medium rounded border text-left transition-colors ${
                      active ? o.cardActiveCls
                             : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700"
                    }`}>
                    <Icon className="w-4 h-4 mb-1"/>
                    <div>{o.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Core data */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Mitarbeiter *</label>
              <div className="mt-1">
                <EmployeePicker value={form.employee_id} testid="bh-emp-picker"
                  onChange={(v) => setForm({ ...form, employee_id: v })} />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Datum {isAnmeldung ? "(Anmeldung)" : isAbmeldung ? "(Abmeldung)" : ""}</label>
              <input type="date" value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="bh-date"
                className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            {isAnmeldung && (
              <>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Stunden / Woche</label>
                  <input type="number" min={1} max={48} value={form.hours_per_week}
                    onChange={(e) => setForm({ ...form, hours_per_week: +e.target.value })}
                    data-testid="bh-hours-per-week"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase text-zinc-500">Position</label>
                  <input value={form.position || ""}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    placeholder="z.B. Fahrer, Lagerarbeiter, Disponent"
                    data-testid="bh-position"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
              </>
            )}
            {isAbmeldung && (
              <div className="col-span-3">
                <label className="text-[10px] uppercase text-zinc-500">Grund (optional)</label>
                <input value={form.termination_reason || ""}
                  onChange={(e) => setForm({ ...form, termination_reason: e.target.value })}
                  placeholder="z.B. Kündigung durch Arbeitnehmer, einvernehmlich, Ablauf befristeter Vertrag"
                  data-testid="bh-termination-reason"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
            )}
          </div>

          {/* To email */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">An (Buchhaltung) *</label>
            <input type="email" value={form.to_email || ""}
              onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              placeholder={config.default_to || "z.B. buchhaltung@steuerberater.at"}
              data-testid="bh-to-email"
              className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.to_email?.trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}/>
            <div className="text-[10px] text-zinc-500 mt-1">
              Standard aus Einstellungen → Buchhaltung: <span className="font-mono text-zinc-400">{config.default_to || "(nicht konfiguriert)"}</span>
            </div>
          </div>

          {/* CC */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">Kopie senden an</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
              {(config.default_cc || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded" title="Standard-Empfänger aus Einstellungen">
                  {e} <span className="text-[9px] uppercase tracking-wide text-blue-400/70">std</span>
                </span>
              ))}
              {userCc.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-zinc-800 text-zinc-200 border border-zinc-700 rounded">
                  {e}
                  <button onClick={() => removeCc(e)} data-testid={`bh-cc-rm-${e}`} className="ml-1 text-zinc-500 hover:text-red-400">
                    <X className="w-3 h-3"/>
                  </button>
                </span>
              ))}
              <div className="inline-flex items-center gap-1">
                <input type="email" placeholder="weitere@email.at" value={newCc}
                  onChange={(e) => setNewCc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCc())}
                  data-testid="bh-cc-input"
                  className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100 w-44"/>
                <button onClick={addCc} data-testid="bh-cc-add"
                  className="px-2 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded">+</button>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase text-zinc-500">Betreff *</label>
              <button onClick={() => loadTemplate(true)} disabled={loadingTpl || !form.employee_id}
                data-testid="bh-tpl-reload"
                className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 disabled:text-zinc-600">
                <RefreshCw className={`w-3 h-3 ${loadingTpl ? "animate-spin" : ""}`}/>
                Vorlage neu laden
              </button>
            </div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="bh-subject"
              className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.subject?.trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}/>
          </div>

          {/* Body */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500">E-Mail-Inhalt</label>
            <textarea rows={14} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="bh-body"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono leading-relaxed"/>
          </div>

          {/* Attachments */}
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block mb-1.5">Anhänge</label>
            {!isEdit ? (
              <div className="px-3 py-2 bg-zinc-950 border border-dashed border-zinc-700 rounded text-[11px] text-zinc-500">
                Bitte zuerst speichern — Anhänge können anschließend hinzugefügt werden.
              </div>
            ) : (
              <>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded cursor-pointer">
                  <Upload className="w-3.5 h-3.5"/>
                  {uploading ? "Lade…" : "Dateien hochladen"}
                  <input type="file" multiple className="hidden" onChange={uploadFiles} data-testid="bh-upload"/>
                </label>
                {form.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {form.attachments.map((a) => (
                      <div key={a.id} data-testid={`bh-att-${a.id}`}
                        className="flex items-center justify-between px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-3 h-3 text-blue-400 shrink-0"/>
                          <span className="truncate text-zinc-200">{a.name}</span>
                          <span className="text-zinc-500 shrink-0">{((a.size || 0) / 1024).toFixed(0)} KB</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => downloadAttachment(a)} className="text-blue-400 hover:text-blue-300">
                            <Download className="w-3.5 h-3.5"/>
                          </button>
                          <button onClick={() => removeAttachment(a.id)} className="text-zinc-500 hover:text-red-400">
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="p-5 border-t border-zinc-800 flex items-center justify-between gap-2 sticky bottom-0 bg-zinc-900">
          <div className="text-[11px] text-zinc-500">
            {isEdit && editing?.status === "gesendet" && editing?.sent_at && (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <Send className="w-3 h-3"/> Gesendet: {fmtDateTime(editing.sent_at)} → {editing.sent_to || "?"}
              </span>
            )}
            {isEdit && editing?.status === "fehlgeschlagen" && (
              <span className="inline-flex items-center gap-1 text-red-400" title={editing.last_send_error || ""}>
                ⚠ Versand fehlgeschlagen
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
            <button onClick={save} disabled={saving || sending} data-testid="bh-save-btn"
              className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-100 border border-zinc-700 rounded font-medium">
              {saving ? "Speichert…" : (isEdit ? "Speichern" : "Als Entwurf speichern")}
            </button>
            {isEdit && (
              <button onClick={sendNow} disabled={saving || sending || !form.to_email?.trim()}
                data-testid="bh-send-btn"
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium inline-flex items-center gap-1.5">
                {sending ? "Sende…" : (<><Send className="w-3.5 h-3.5"/> Jetzt senden</>)}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
